import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { searchPeople, type SearchHit } from '@/lib/api/search'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/cn'

/**
 * The one search box (admin revamp, 31 Oct 2026; plan mock-up 5). Omar: "build it all in one go".
 * Finds a student (or, for a school's two roles and the platform admin, a staff account) by name,
 * e-mail, phone or id -- the whole id or its first characters. Each role sees its own scope only;
 * the server decides it (GET /admin/search). "/" or Ctrl/Cmd+K focuses it; ↑ ↓ move; Enter opens;
 * Esc closes. A student opens the one student page; a staff account opens Staff searched for him.
 */
export function SearchBox() {
  const role = useAuthStore((s) => s.user?.role)
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
        e.preventDefault()
        input.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const res = useQuery({
    queryKey: ['search', q],
    queryFn: () => searchPeople(q),
    enabled: q.length >= 2,
    retry: false,
    staleTime: 30_000,
  })
  const hits = useMemo(
    () => [...(res.data?.students ?? []).map((h) => ({ h, kind: 'student' as const })),
      ...(res.data?.staff ?? []).map((h) => ({ h, kind: 'staff' as const }))],
    [res.data],
  )
  useEffect(() => setCursor(0), [q])

  const openHit = (hit: SearchHit, kind: 'student' | 'staff') => {
    setOpen(false)
    setText('')
    if (kind === 'student') navigate(`/students/${hit.id}`)
    else navigate(`/users?q=${encodeURIComponent(hit.name)}`)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); input.current?.blur(); return }
    if (!hits.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, hits.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)) }
    if (e.key === 'Enter') { e.preventDefault(); const x = hits[cursor]; if (x) openHit(x.h, x.kind) }
  }

  const placeholder = role === 'TEACHER' ? 'Search my students…' : role === 'ADMIN' ? 'Search student by name, email, phone or ID…' : 'Search a student in your school…'
  let i = -1

  return (
    <div className="relative w-full max-w-md" data-testid="search-box">
      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={open && q.length >= 2}
        aria-label="Search"
        value={text}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        className="h-10 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-body-md focus:bg-white"
      />
      {open && q.length >= 2 && (
        <div className="absolute left-0 right-0 top-11 z-40 max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-pop" role="listbox" data-testid="search-results">
          {res.isLoading && <p className="px-3 py-2 text-body-sm text-slate-400">Searching…</p>}
          {res.isError && <p className="px-3 py-2 text-body-sm text-danger">Search failed</p>}
          {res.data && hits.length === 0 && <p className="px-3 py-2 text-body-sm text-slate-400">No one found</p>}
          {(['student', 'staff'] as const).map((kind) => {
            const group = hits.filter((x) => x.kind === kind)
            if (!group.length) return null
            return (
              <div key={kind}>
                <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {kind === 'student' ? 'Students' : 'Staff'} · {group.length}
                </p>
                {group.map(({ h }) => {
                  i += 1
                  const idx = i
                  return (
                    <button
                      key={h.id}
                      type="button"
                      role="option"
                      aria-selected={idx === cursor}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => openHit(h, kind)}
                      className={cn('flex w-full items-center gap-3 rounded px-3 py-2 text-left', idx === cursor ? 'bg-primary-soft' : 'hover:bg-slate-50')}
                    >
                      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-primary text-[12px] font-bold text-white">
                        {h.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-body-md font-medium text-slate-800" dir="auto">{h.name}</span>
                        <span className="block truncate text-label-md text-slate-500" dir="auto">
                          {kind === 'staff' ? h.role.replace('_', ' ').toLowerCase() : h.grade_name ?? '—'}
                          {h.school_name ? ` · ${h.school_name}` : ''} · ID {h.id.slice(0, 8)}… · {h.matched} match
                        </span>
                      </span>
                      {h.is_test && <span className="rounded-[5px] bg-warning px-1 text-[10px] font-bold text-white">TEST</span>}
                    </button>
                  )
                })}
              </div>
            )
          })}
          <p className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400">↑↓ to move · Enter to open · Esc to close</p>
        </div>
      )}
    </div>
  )
}
